import { useEffect, useState } from "react";

import {
  type ApiDeps,
  type ApprovalEntry,
  type ApprovalQuery,
  fetchApprovals,
} from "../lib/api";
import { t, type Language, type StringKey } from "../lib/i18n";
import { CorrelationId } from "./CorrelationId";

interface ApprovalsHistoryProps {
  deps: ApiDeps;
  language: Language;
  /** Shared refresh signal — bump to refetch. Defaults to 0 so the
   * component renders standalone (e.g. in unit tests). */
  reloadKey?: number;
}

type ListState =
  | { kind: "loading" }
  | { kind: "loaded"; items: ApprovalEntry[] }
  | { kind: "error" };

const STATUS_LABEL_KEY: Record<ApprovalEntry["status"], StringKey> = {
  pending: "approvals.status.pending",
  approved: "approvals.status.approved",
  aborted: "approvals.status.aborted",
};

// Per-status chip styling. Pending vs approved must NOT differ by colour
// alone (A4 / WCAG 1.4.1): pending is an outlined neutral pill, approved a
// solid accent pill, aborted a red pill — and each carries a shape glyph.
const STATUS_CHIP_CLASS: Record<ApprovalEntry["status"], string> = {
  pending: "bg-kwanza-surface text-kwanza-inkSoft ring-1 ring-inset ring-kwanza-divider",
  approved: "bg-kwanza-accentSoft text-kwanza-accentText",
  aborted: "bg-kwanza-dangerTint text-kwanza-alert",
};

// Decorative shape cue paired with the text label (aria-hidden — the label
// already conveys the state to assistive tech; this is for colour-blind
// sighted users so pending/approved aren't distinguished by hue alone).
const STATUS_GLYPH: Record<ApprovalEntry["status"], string> = {
  pending: "⏳",
  approved: "✓",
  aborted: "✕",
};

/** Tier-C approval history (per F37 spec §5.2 row 4): a `pending` /
 * `recent` toggle over the approvals route. Pending shows cards still
 * waiting (with how long); recent shows decisions (with who decided).
 * Self-fetching; re-fetches when the toggle flips. */
export function ApprovalsHistory({
  deps,
  language,
  reloadKey = 0,
}: ApprovalsHistoryProps): JSX.Element {
  const [mode, setMode] = useState<ApprovalQuery>("pending");
  const [state, setState] = useState<ListState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    fetchApprovals(deps, mode, 50)
      .then((items) => {
        if (!cancelled) setState({ kind: "loaded", items });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [deps.backendUrl, deps.getAuthToken, mode, reloadKey]);

  const tab = (value: ApprovalQuery, labelKey: StringKey): JSX.Element => (
    <button
      type="button"
      onClick={() => setMode(value)}
      aria-pressed={mode === value}
      data-testid={`approvals-tab-${value}`}
      className={
        "rounded px-3 py-1 text-sm font-medium " +
        (mode === value
          ? "bg-kwanza-accent font-bold text-kwanza-onAccent"
          : "border border-kwanza-divider bg-kwanza-card text-kwanza-inkSoft")
      }
    >
      {t(labelKey, language)}
    </button>
  );

  return (
    <section
      aria-label={t("approvals.heading", language)}
      className="rounded-[2px] border border-kwanza-divider bg-kwanza-card p-4 shadow-sm"
    >
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold text-kwanza-ink">
          {t("approvals.heading", language)}
        </h2>
        <div className="flex gap-2">
          {tab("pending", "approvals.tab.pending")}
          {tab("recent", "approvals.tab.recent")}
        </div>
      </div>

      {state.kind === "loading" ? (
        <ul aria-busy="true" data-testid="approvals-skeleton">
          {[0, 1, 2].map((i) => (
            <li
              key={i}
              className="mb-2 h-6 animate-pulse rounded bg-kwanza-divider"
            />
          ))}
        </ul>
      ) : state.kind === "error" ? (
        <p className="text-sm text-kwanza-alert" data-testid="approvals-error">
          {t("error.fetch_failed.body", language)}
        </p>
      ) : state.items.length === 0 ? (
        <p className="text-sm text-kwanza-inkSoft" data-testid="approvals-empty">
          {t(
            mode === "pending"
              ? "approvals.empty.pending"
              : "approvals.empty.recent",
            language,
          )}
        </p>
      ) : (
        <ul className="divide-y divide-kwanza-divider">
          {state.items.map((item) => (
            <li
              key={`${item.correlation_id}-${item.occurred_at}`}
              className="flex items-baseline justify-between gap-4 py-2"
              data-testid="approval-item"
            >
              <div className="flex flex-col">
                <span className="text-sm text-kwanza-ink">
                  {item.subject ?? (
                    <CorrelationId
                      value={item.correlation_id}
                      language={language}
                    />
                  )}
                </span>
                <span className="text-xs text-kwanza-inkSoft">
                  {item.status === "pending" && item.age_hours !== null
                    ? `${Math.round(item.age_hours)}h ${t("approvals.waiting", language)}`
                    : item.actor
                      ? `${t("approvals.decided_by", language)}: ${item.actor}`
                      : new Date(item.occurred_at).toLocaleString(language)}
                </span>
              </div>
              <span
                className={
                  "inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium " +
                  STATUS_CHIP_CLASS[item.status]
                }
                data-testid="approval-status"
              >
                <span aria-hidden="true">{STATUS_GLYPH[item.status]}</span>
                {t(STATUS_LABEL_KEY[item.status], language)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
